import { defineStore } from 'pinia';
import { ref } from 'vue';
import requests from '../../../_00_elements/tools/requests';
import {
  URL_XHR_CREATE_CHECKOUT_SESSION,
  URL_XHR_CREDIT_NOTE_ENDPOINT,
  URL_XHR_INVOICE_REMAINING,
  URL_XHR_INVOICES_ENDPOINT,
  URL_XHR_UPDATE_INVOICE_STATUS_ENDPOINT,
} from '../../../_00_elements/tools/routes/routes';
import { trans, transExists } from '../../../_00_elements/translation';
import { flashBagError, flashBagSuccess } from '../../../_00_elements/tools/flashbag';

const useInvoiceStore = defineStore('invoice', () => {
  const invoices = ref([]);
  const selectedInvoice = ref(null);
  const loading = ref(false);

  const setInvoices = (value) => {
    invoices.value = value;
  };

  const setSelectedInvoice = (value) => {
    selectedInvoice.value = value;
  };

  const updateInvoiceById = (id, invoice) => {
    const index = invoices.value.findIndex((item) => item.id === id);

    if (index >= 0) {
      invoices.value.splice(index, 1, invoice);
    }
  };

  const createCheckoutSession = async (price, invoiceId) => {
    const payment = {
      price,
      invoiceId,
    };

    try {
      loading.value = true;
      const response = await requests.post(URL_XHR_CREATE_CHECKOUT_SESSION, payment);

      return response?.data?.reload;
    } catch (error) {
      flashBagError(error.response?.data?.message);
    } finally {
      loading.value = false;
    }

    return false;
  };

  const createInvoice = async (subscriptionId, title, price) => {
    try {
      loading.value = true;
      const response = await requests.post(
        URL_XHR_INVOICES_ENDPOINT,
        {
          subscriptionId,
          title,
          price,
        },
      );

      flashBagSuccess(trans('gestion.invoices.slider.invoice.flashbag.success'));

      return response.data;
    } catch (error) {
      flashBagError(error.response?.data?.message);
    } finally {
      loading.value = false;
    }

    return null;
  };

  const updateInvoiceStatus = async (invoiceType, invoiceId, status) => {
    try {
      loading.value = true;

      const endpoint = URL_XHR_UPDATE_INVOICE_STATUS_ENDPOINT
        .replace('{type}', invoiceType)
        .replace('{id}', invoiceId)
        .replace('{status}', status.toUpperCase());

      const { data } = await requests.post(endpoint);

      return data;
    } catch (error) {
      flashBagError(error.response?.data?.message);
    } finally {
      loading.value = false;
    }

    return null;
  };

  const createCreditNote = async (clientInvoiceId, invoiceLabel, amount) => {
    try {
      loading.value = true;

      const { data } = await requests.post(
        URL_XHR_CREDIT_NOTE_ENDPOINT,
        {
          clientInvoiceId,
          invoiceLabel,
          amount,
        },
      );

      loading.value = false;

      return data;
    } catch (error) {
      let message = error.response?.data?.message ?? trans('global.error');

      if (error.response?.status === 422) {
        const unprocessableMessage = `gestion.credit_note.${error.response?.data?.message}`;

        if (transExists(unprocessableMessage)) {
          message = trans(unprocessableMessage);
        }
      }

      flashBagError(message);
      loading.value = false;
    }

    return null;
  };

  const getMaxRefundableAmount = async (invoiceId) => {
    try {
      loading.value = true;

      const endpoint = URL_XHR_INVOICE_REMAINING.replace('{id}', invoiceId);
      const { data } = await requests.get(endpoint);

      loading.value = false;

      return Math.round(data.remaining_amount * 100) / 100;
    } catch (error) {
      flashBagError(error.response?.data?.message ?? trans('global.error'));
      loading.value = false;
    }

    return null;
  };

  const isLoading = () => loading.value === true;

  return {
    invoices,
    selectedInvoice,
    setInvoices,
    setSelectedInvoice,
    isLoading,
    createInvoice,
    createCheckoutSession,
    createCreditNote,
    getMaxRefundableAmount,
    updateInvoiceById,
    updateInvoiceStatus,
  };
});

export default useInvoiceStore;
